import { createSlice } from "@reduxjs/toolkit";

const REQUEST_STATUS = {
  success: "success",
  loading: "loading",
  error: "error",
};

const DefaultStep = {
  0: {
    id: 0,
    duration: 4,
    title: "Uploading data",
    status: REQUEST_STATUS.loading,
  },
};

/**
 * @type {Project}
 */
const defaultProject = {
  dataset: null,
  dataTraining: {
    currentStepIndex: 0,
    form: {
      model_name: "",
      target: "",
      test_data: "",
      type: "",
    },
    isError: false,
    isLoading: false,
    isTraining: false,
    output: null,
    predict: {
      currentStepIndex: 0,
      startTraining: false,
      isError: false,
      predict_output: null,
      steps: DefaultStep,
      pipeline: null
    },
    steps: DefaultStep,
  },
  dataUpload: {
    currentStepIndex: 0,
    isCompleted: false,
    isDataStatus: true,
    isError: false,
    isLoading: false,
    isShowDataset: false,
    output: null,
    steps: DefaultStep,
  },
  page: 0,
};

/**
 * @type {WizardSlice}
 */
const initialState = {
  projectId: null,
  projects: null,
};

export const wizardSlice = createSlice({
  name: "wizard",
  initialState,
  reducers: {
    addProject: (state, action) => {
      state.projectId = action.payload;
      const project = state.projects;

      state.projects = { ...project, [action.payload]: defaultProject };
    },
    setDataset: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataset = action.payload;
    },
    setPage: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].page = action.payload;
    },
    setAnlOutput: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.output = action.payload;
    },
    setUploadStep: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.steps = {
        ...project.dataUpload.steps,
        ...action.payload,
      };
    },
    setUploadComplete: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.isCompleted = action.payload;
    },
    setUploadStatus: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.isDataStatus = action.payload;
    },
    setUploadIsLoading: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.isLoading = action.payload;
    },
    setUploadDataset: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.isShowDataset = action.payload;
    },
    setUploadCurrentStepIndex: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.currentStepIndex = action.payload;
    },
    setUploadError: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.isError = action.payload;
    },
    resetUploadStep: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataUpload.steps = DefaultStep;
    },

    // data training functions
    setTrainLoading: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.isLoading = action.payload;
    },
    setTraining: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.isTraining = action.payload;
    },
    setTrainError: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.isError = action.payload;
    },
    setTraining: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.isTraining = action.payload;
    },
    resetTrainStep: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.steps = DefaultStep;
    },
    setTrainPredictSteps: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.predict.steps = action.payload;
    },
    setTrainForm: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.form[action.payload.name] =
        action.payload.value;
    },
    setTrainOutput: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.output = action.payload;
    },

    // data predict
    setTrainPredictStart: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.predict.startTraining =
        action.payload;
    },
    setTrainPredictError: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.predict.isError = action.payload;
    },
    setTrainPredict: (state, action) => {
      state.dataTraining.predict;
    },
    setTrainPredictOutput: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.predict.predict_output =
        action.payload;
    },
    setTrainPredictPipeline: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.predict.pipeline =
        action.payload;
    },
    resetTrainPredictStep: (state, action) => {
      const projectId = state.projectId;
      const project = state.projects[projectId];

      state.projects[projectId].dataTraining.steps = DefaultStep;
    },
  },
});

export const {
  addProject,
  setDataset,
  setPage,
  setAnlOutput,
  setUploadStep,
  setUploadComplete,
  setUploadCurrentStepIndex,
  setUploadDataset,
  setUploadIsLoading,
  setUploadStatus,
  setUploadError,
  resetUploadStep,

  // data training
  setTrainLoading,
  setTraining,
  setTrainError,
  resetTrainStep,
  setTrainPredictStart,
  setTrainPredictError,
  resetTrainPredictStep,
  setTrainPredictOutput,
  setTrainForm,
  setTrainOutput,
  setTrainPredictPipeline
} = wizardSlice.actions;
export const reducer = wizardSlice.reducer;

/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectPage = (state) =>
  state.wizard.projects?.[state?.wizard?.projectId]?.page;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectDataset = (state) =>
  state.wizard.projects?.[state?.wizard?.projectId]?.dataset;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectAnlOutput = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.output;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadError = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.isError;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadLoading = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.isLoading;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadStatus = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.isDataStatus;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadDataset = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.isShowDataset;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadSteps = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.steps;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadStepIndex = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.currentStepIndex;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectUploadCompleted = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataUpload.isCompleted;

/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingOutput = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining.output;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingSteps = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining.steps;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingStepIndex = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining
    ?.currentStepIndex;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingErr = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining?.isError;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingPredict = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining?.predict;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingLoading = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining.isLoading;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingForm = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining.form;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingTrain = (state) =>
  state.wizard.projects?.[state.wizard?.projectId]?.dataTraining.isTraining;
/**
 *
 * @param {{ wizard: WizardSlice }} state
 * @returns
 */
export const selectTrainingPipeline = (state) => state.wizard.projects?.[state.wizard.projectId]?.dataTraining?.predict.pipeline